// AxisCare Import — one-off (re-runnable) copy of AxisCare's visit history
// and schedule patterns into CAERA, so nothing is lost at cut-over.
//
// Preview is a dry run: it fetches and maps everything and reports what
// would happen, writing nothing. Import writes. Both list every AxisCare
// client, caregiver or service that could not be matched, so the fix is a
// data fix (link the record, add the catalog code) and a re-run.
import { useCallback, useEffect, useState } from 'react';
import {
  fetchAxisCareVisits, fetchAxisCareSchedules, importVisits, importSchedules, undoImport,
  getImportRuns, getImportedCounts, isoDate, addDays, monthChunks, mergeVisitResults,
} from '../../lib/axiscareImport';

const field = { padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 11, color: 'var(--text3)', marginBottom: 5 };
const check = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text2)', cursor: 'pointer' };
const tile = { padding: '12px 14px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', minWidth: 120 };
const th = { textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)' };
const td = { fontSize: 12, padding: '6px 8px', borderBottom: '0.5px solid var(--border)', verticalAlign: 'top' };

const STATUS_LABEL = { completed: 'Completed', missed: 'Missed', cancelled: 'Cancelled (rescheduled)', scheduled: 'Scheduled (future)', in_progress: 'In progress' };
const titleCase = (s) => (s || '').replace(/\b\w/g, c => c.toUpperCase());

export default function AxisCareImport() {
  const today = isoDate(new Date());
  const [from, setFrom] = useState('2026-01-01');
  const [to, setTo] = useState(addDays(today, 28));
  const [doVisits, setDoVisits] = useState(true);
  const [doSchedules, setDoSchedules] = useState(true);
  const [linkByName, setLinkByName] = useState(true);
  const [running, setRunning] = useState(null);       // 'preview' | 'import' | 'undo'
  const [log, setLog] = useState([]);
  const [visitSummary, setVisitSummary] = useState(null);
  const [schedSummary, setSchedSummary] = useState(null);
  const [error, setError] = useState('');
  const [runs, setRuns] = useState([]);
  const [counts, setCounts] = useState({ visits: 0, schedules: 0 });

  const refresh = useCallback(async () => {
    const [{ data }, c] = await Promise.all([getImportRuns(), getImportedCounts()]);
    setRuns(data || []); setCounts(c);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const say = (line) => setLog(l => [...l, `${new Date().toLocaleTimeString()}  ${line}`]);

  const run = async (dryRun) => {
    if (from > to) { setError('The start date is after the end date.'); return; }
    setRunning(dryRun ? 'preview' : 'import'); setError(''); setLog([]); setVisitSummary(null); setSchedSummary(null);
    const opts = { dryRun, linkByName };
    try {
      if (doVisits) {
        let acc = null;
        for (const [a, b] of monthChunks(from, to)) {
          say(`Fetching AxisCare visits ${a} → ${b}…`);
          const rows = await fetchAxisCareVisits(a, b);
          if (!rows.length) { say('  no visits in AxisCare for this period'); continue; }
          say(`  ${rows.length} visits received, ${dryRun ? 'checking' : 'importing'}…`);
          const r = await importVisits(rows, opts);
          say(`  ${r.inserted} new, ${r.updated} updated, ${r.kept_edited_in_caera} kept (edited in CAERA), ${r.skipped_unmatched} unmatched`);
          acc = mergeVisitResults(acc, r);
          setVisitSummary({ ...acc });
        }
      }
      if (doSchedules) {
        say(`Fetching AxisCare schedule patterns active ${today} → ${addDays(today, 28)}…`);
        const rows = await fetchAxisCareSchedules(today, addDays(today, 28));
        say(`  ${rows.length} weekday rows received, ${dryRun ? 'checking' : 'importing'}…`);
        const r = await importSchedules(rows, opts);
        say(`  ${r.inserted} new patterns, ${r.updated} updated, ${r.skipped_unmatched_rows} unmatched rows, ${r.visits_linked} visits linked to their pattern`);
        setSchedSummary(r);
      }
      say(dryRun ? 'Preview finished. Nothing was written.' : 'Import finished.');
    } catch (e) {
      setError(e.message || String(e)); say(`Stopped: ${e.message || e}`);
    } finally {
      setRunning(null); refresh();
    }
  };

  const undo = async () => {
    if (!window.confirm(`Remove every imported AxisCare visit and paused imported schedule from CAERA?\n\nVisits the office has since edited or clocked in CAERA are kept. This does not touch AxisCare.`)) return;
    setRunning('undo'); setError('');
    try { const r = await undoImport(); say(`Undo: ${r.visits_removed} visits and ${r.schedules_removed} schedules removed.`); }
    catch (e) { setError(e.message || String(e)); }
    finally { setRunning(null); refresh(); }
  };

  const unmatched = visitSummary ? {
    clients: Object.values(visitSummary.unmatched.clients), caregivers: Object.values(visitSummary.unmatched.caregivers), services: Object.values(visitSummary.unmatched.services),
  } : null;
  const schedUnmatched = schedSummary?.unmatched;
  const anyUnmatched = (unmatched && (unmatched.clients.length || unmatched.caregivers.length || unmatched.services.length))
    || (schedUnmatched && (schedUnmatched.clients.length || schedUnmatched.caregivers.length || schedUnmatched.services.length));

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>AxisCare Import</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.55, maxWidth: 760 }}>
          Copies AxisCare's visit history and schedule patterns into CAERA so nothing is lost at cut-over. Past visits arrive already verified and become
          the continuous history the day a service line is switched to CAERA in the Service Catalog. Until then they stay out of billing, payroll and
          the EVV feed. Schedule patterns arrive <b>paused</b>; activate them on Schedule (CAERA) at cut-over. Safe to run again: a second run updates
          rather than duplicates, and anything the office has since edited in CAERA is kept.
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <div style={tile}><div style={label}>Imported visits in CAERA</div><div style={{ fontSize: 20, fontWeight: 600 }}>{counts.visits.toLocaleString()}</div></div>
          <div style={tile}><div style={label}>Imported schedule patterns</div><div style={{ fontSize: 20, fontWeight: 600 }}>{counts.schedules.toLocaleString()}</div></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><div style={label}>Visits from</div><input type="date" style={field} value={from} onChange={e => setFrom(e.target.value)} disabled={!!running} /></div>
          <div><div style={label}>to</div><input type="date" style={field} value={to} onChange={e => setTo(e.target.value)} disabled={!!running} /></div>
          <div style={{ display: 'grid', gap: 8, paddingBottom: 4 }}>
            <label style={check}><input type="checkbox" checked={doVisits} onChange={e => setDoVisits(e.target.checked)} disabled={!!running} /> Visits (history and upcoming, month by month)</label>
            <label style={check}><input type="checkbox" checked={doSchedules} onChange={e => setDoSchedules(e.target.checked)} disabled={!!running} /> Schedule patterns currently active in AxisCare</label>
            <label style={check}><input type="checkbox" checked={linkByName} onChange={e => setLinkByName(e.target.checked)} disabled={!!running} /> When a client or caregiver has no AxisCare id in CAERA, match by exact full name and save the id</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => run(true)} disabled={!!running || (!doVisits && !doSchedules)}>{running === 'preview' ? 'Previewing…' : 'Preview (dry run)'}</button>
          <button className="btn btn-primary" onClick={() => run(false)} disabled={!!running || (!doVisits && !doSchedules)}>{running === 'import' ? 'Importing…' : 'Import now'}</button>
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={undo} disabled={!!running || (!counts.visits && !counts.schedules)} style={{ color: 'var(--danger)' }}>{running === 'undo' ? 'Removing…' : 'Undo import'}</button>
        </div>
        {error && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
        {log.length > 0 && (
          <pre style={{ marginTop: 12, padding: 10, borderRadius: 7, background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: 11, lineHeight: 1.5, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'var(--mono)' }}>
            {log.join('\n')}
          </pre>
        )}
      </div>

      {visitSummary && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Visits {visitSummary.dry_run ? '(preview)' : ''}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={tile}><div style={label}>AxisCare rows</div><div style={{ fontSize: 18, fontWeight: 600 }}>{visitSummary.rows.toLocaleString()}</div></div>
            <div style={tile}><div style={label}>New in CAERA</div><div style={{ fontSize: 18, fontWeight: 600 }}>{visitSummary.inserted.toLocaleString()}</div></div>
            <div style={tile}><div style={label}>Updated</div><div style={{ fontSize: 18, fontWeight: 600 }}>{visitSummary.updated.toLocaleString()}</div></div>
            <div style={tile}><div style={label}>Kept (edited in CAERA)</div><div style={{ fontSize: 18, fontWeight: 600 }}>{visitSummary.kept_edited_in_caera.toLocaleString()}</div></div>
            <div style={tile}><div style={label}>Skipped, unmatched</div><div style={{ fontSize: 18, fontWeight: 600, color: visitSummary.skipped_unmatched ? 'var(--danger)' : 'inherit' }}>{visitSummary.skipped_unmatched.toLocaleString()}</div></div>
            <div style={tile}><div style={label}>Linked by name</div><div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>{visitSummary.linked_by_name.clients} clients · {visitSummary.linked_by_name.caregivers} caregivers</div></div>
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text2)' }}>
            {Object.entries(visitSummary.by_status).map(([k, n]) => <span key={k}><b>{n.toLocaleString()}</b> {STATUS_LABEL[k] || k}</span>)}
          </div>
        </div>
      )}

      {schedSummary && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Schedule patterns {schedSummary.dry_run ? '(preview)' : ''}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={tile}><div style={label}>AxisCare weekday rows</div><div style={{ fontSize: 18, fontWeight: 600 }}>{schedSummary.rows}</div></div>
            <div style={tile}><div style={label}>New patterns</div><div style={{ fontSize: 18, fontWeight: 600 }}>{schedSummary.inserted}</div></div>
            <div style={tile}><div style={label}>Updated</div><div style={{ fontSize: 18, fontWeight: 600 }}>{schedSummary.updated}</div></div>
            <div style={tile}><div style={label}>Unmatched rows</div><div style={{ fontSize: 18, fontWeight: 600, color: schedSummary.skipped_unmatched_rows ? 'var(--danger)' : 'inherit' }}>{schedSummary.skipped_unmatched_rows}</div></div>
            <div style={tile}><div style={label}>Visits linked to a pattern</div><div style={{ fontSize: 18, fontWeight: 600 }}>{schedSummary.visits_linked}</div></div>
          </div>
        </div>
      )}

      {anyUnmatched ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Could not be matched</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
            These AxisCare records have no CAERA counterpart by id or by exact full name, so their visits were skipped. Add the AxisCare id to the
            client or personnel record (or fix the spelling so the names match), add the AxisCare service code to the Service Catalog line, then run again.
          </div>
          <UnmatchedTable title="Clients" rows={[...(unmatched?.clients || []), ...(schedUnmatched?.clients || [])]} idLabel="AxisCare client id" />
          <UnmatchedTable title="Caregivers" rows={[...(unmatched?.caregivers || []), ...(schedUnmatched?.caregivers || [])]} idLabel="AxisCare caregiver id" />
          <UnmatchedTable title="Services" rows={[...(unmatched?.services || []), ...(schedUnmatched?.services || [])].map(s => ({ axiscare_id: s.code, name: s.name }))} idLabel="AxisCare service code" nameLabel="Description" />
        </div>
      ) : null}

      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Recent runs</div>
        {runs.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No runs yet.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>When</th><th style={th}>What</th><th style={th}>Range</th><th style={th}>Mode</th><th style={th}>Result</th><th style={th}>By</th></tr></thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id}>
                    <td style={td}>{new Date(r.run_at).toLocaleString()}</td>
                    <td style={td}>{r.kind === 'axiscare_visits' ? 'Visits' : 'Schedules'}</td>
                    <td style={{ ...td, fontFamily: 'var(--mono)' }}>{r.range_start || '—'}{r.range_end ? ` → ${r.range_end}` : ''}</td>
                    <td style={td}><span className={`badge ${r.dry_run ? 'badge-low' : 'badge-done'}`}>{r.dry_run ? 'Preview' : 'Import'}</span></td>
                    <td style={td}>
                      {r.kind === 'axiscare_visits'
                        ? `${r.result.rows} rows · ${r.result.inserted} new · ${r.result.updated} updated · ${r.result.skipped_unmatched} unmatched`
                        : `${r.result.rows} rows · ${r.result.inserted} new · ${r.result.updated} updated · ${r.result.skipped_unmatched_rows} unmatched`}
                    </td>
                    <td style={td}>{r.run_by?.full_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UnmatchedTable({ title, rows, idLabel, nameLabel = 'Name in AxisCare' }) {
  const seen = new Set();
  const uniq = rows.filter(r => { const k = `${r.axiscare_id}|${r.name}`; if (seen.has(k)) return false; seen.add(k); return true; });
  if (!uniq.length) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, margin: '8px 0 4px' }}>{title} · {uniq.length}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: 560 }}>
        <thead><tr><th style={th}>{idLabel}</th><th style={th}>{nameLabel}</th></tr></thead>
        <tbody>{uniq.map(r => <tr key={`${r.axiscare_id}|${r.name}`}><td style={{ ...td, fontFamily: 'var(--mono)' }}>{r.axiscare_id ?? '—'}</td><td style={td}>{titleCase(r.name)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}
